// The employee feedback dialog, opened from the floating FeedbackLauncher.
//
// English-only on purpose: every employee surface in this SPA is (the
// impersonation banner, /employee/jobs, /employee/customers). The customer journey is
// the translated half.
//
// The submission goes straight to Nexus — the portal keeps no copy — so the
// reference number in the success state is the only handle the reporter gets.
// It is shown until they dismiss the dialog rather than flashed and lost.
//
// A screenshot taken here is of an impersonated session and therefore contains
// that customer's data. It lands in Nexus's internal queue and is sent to the
// configured AI provider as image input — a deliberate, approved tradeoff,
// because the screenshot is most of what makes a report actionable.

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  FEEDBACK_CATEGORIES,
  MAX_FEEDBACK_SCREENSHOTS,
  MAX_FEEDBACK_SCREENSHOT_BYTES,
  MAX_FEEDBACK_SCREENSHOT_TOTAL_BYTES,
  type FeedbackCategory,
} from 'shared';

import { extractApiError } from '../../api/client.js';
import { submitFeedback } from '../../api/feedback.js';
import {
  CAPTURE_EXCLUDE_ATTR,
  captureViewport,
  readImageFile,
  type CapturedImage,
} from './captureScreenshot.js';
import { resolvePageContext } from './pageContext.js';
import { PASTE_HINT_SUPPORTED, PASTE_SHORTCUT, usePasteFiles } from '../usePasteFiles.js';

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: 'Something is broken',
  feature_request: 'Feature request',
  training_question: 'Confusing — how do I…?',
  data_issue: 'Wrong or missing data',
  permission_issue: 'Access / permission problem',
};

// Windows often reports no mime type for HEIC / HEIF / AVIF, so the extension
// stands in for it — the same three the picker's `accept` names.
function isAttachableImage(file: File): boolean {
  return file.type.startsWith('image/') || /\.(heic|heif|avif)$/i.test(file.name);
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function FeedbackDialog({ open, onClose }: Props): JSX.Element | null {
  const location = useLocation();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState<CapturedImage[]>([]);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filed, setFiled] = useState<{ feedback_number: string | null } | null>(null);
  const [autoCaptured, setAutoCaptured] = useState(false);

  // Reset on each open so a previous report never bleeds into the next one.
  useEffect(() => {
    if (!open) return;
    setCategory('');
    setSummary('');
    setDescription('');
    setNotes('');
    setImages([]);
    setError(null);
    setFiled(null);
    setAutoCaptured(false);
    dialogRef.current?.focus();
  }, [open]);

  // Capture the screen they were looking at when they opened the dialog. On
  // open rather than on demand is the point: by the time someone has finished
  // describing a problem they have usually navigated away from it.
  useEffect(() => {
    if (!open || autoCaptured) return;
    setAutoCaptured(true);
    setCapturing(true);
    void (async () => {
      try {
        const shot = await captureViewport('screenshot.png');
        setImages((prev) => (prev.length ? prev : [shot]));
      } catch {
        // A failed capture is not a failed report — they can attach a file.
      } finally {
        setCapturing(false);
      }
    })();
  }, [open, autoCaptured]);

  // Left on at the attachment cap so a paste there gets addImage's "at most N"
  // message rather than doing nothing.
  const pasteProps = usePasteFiles((files) => onPickFiles(files), open && filed === null);

  if (!open) return null;

  const totalBytes = images.reduce((sum, img) => sum + img.size, 0);

  function addImage(shot: CapturedImage): void {
    if (images.length >= MAX_FEEDBACK_SCREENSHOTS) {
      setError(`You can attach at most ${MAX_FEEDBACK_SCREENSHOTS} images.`);
      return;
    }
    if (shot.size > MAX_FEEDBACK_SCREENSHOT_BYTES) {
      setError('That image is too large to attach.');
      return;
    }
    if (totalBytes + shot.size > MAX_FEEDBACK_SCREENSHOT_TOTAL_BYTES) {
      setError('Those images are too large to attach together.');
      return;
    }
    setError(null);
    setImages((prev) => [...prev, shot]);
  }

  function recapture(): void {
    setCapturing(true);
    setError(null);
    void (async () => {
      try {
        const shot = await captureViewport(`screenshot-${images.length + 1}.png`);
        addImage(shot);
      } catch {
        setError('Could not capture the screen. Attach an image instead.');
      } finally {
        setCapturing(false);
      }
    })();
  }

  function onPickFiles(files: ArrayLike<File> | null): void {
    if (!files?.length) return;
    void (async () => {
      for (const file of Array.from(files)) {
        // The picker's `accept` filters a browse, but nothing filters a paste or
        // a file copied in Explorer, and the backend takes any mime type.
        if (!isAttachableImage(file)) {
          setError('Only images can be attached.');
          continue;
        }
        try {
          addImage(await readImageFile(file));
        } catch {
          setError('Could not read that file.');
        }
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    })();
  }

  function onSubmit(): void {
    if (!category || !summary.trim() || !description.trim()) {
      setError('Pick a category and fill in both the summary and the description.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const page = resolvePageContext(location.pathname, window.location.href);
    void (async () => {
      try {
        const result = await submitFeedback({
          category,
          summary: summary.trim(),
          description: description.trim(),
          additional_notes: notes.trim() || null,
          module: page.module,
          page_name: page.page_name,
          page_url: page.page_url,
          screenshots: images.map((img) => ({
            filename: img.filename,
            mime_type: img.mime_type,
            content_base64: img.content_base64,
          })),
        });
        setFiled({ feedback_number: result.feedback_number });
      } catch (err) {
        setError(extractApiError(err)?.message ?? 'Could not file your report. Please try again.');
      } finally {
        setSubmitting(false);
      }
    })();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const node = dialogRef.current;
    if (!node) return;
    const focusables = Array.from(
      node.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const fieldStyle = { width: '100%', marginTop: 4 } as const;
  const labelStyle = { display: 'block', marginTop: 12, fontWeight: 600 } as const;

  return (
    <div
      {...{ [CAPTURE_EXCLUDE_ATTR]: '' }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
        zIndex: 200,
      }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Report a problem or request"
        tabIndex={-1}
        {...pasteProps}
        className="card"
        style={{ maxWidth: 600, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>Report a problem or request</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {filed ? (
          <div style={{ marginTop: 16 }}>
            <p>
              Thanks — this is filed in Nexus
              {filed.feedback_number ? (
                <>
                  {' '}
                  as <strong>{filed.feedback_number}</strong>
                </>
              ) : null}
              . The team triages it there and will reply on the thread.
            </p>
            <button type="button" onClick={onClose} style={{ marginTop: 12 }}>
              Done
            </button>
          </div>
        ) : (
          <>
            <p style={{ color: 'var(--color-text-muted)', marginTop: 8 }}>
              Filed under your Argo account, not the customer&apos;s. The screen behind this dialog
              is attached so the team can see what you saw.
            </p>

            <label style={labelStyle} htmlFor="feedback-category">
              What kind of report is this?
            </label>
            <select
              id="feedback-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory | '')}
              style={fieldStyle}
            >
              <option value="">Choose one…</option>
              {FEEDBACK_CATEGORIES.map((key) => (
                <option key={key} value={key}>
                  {CATEGORY_LABELS[key]}
                </option>
              ))}
            </select>

            <label style={labelStyle} htmlFor="feedback-summary">
              One-line summary
            </label>
            <input
              id="feedback-summary"
              type="text"
              value={summary}
              maxLength={300}
              onChange={(e) => setSummary(e.target.value)}
              style={fieldStyle}
            />

            <label style={labelStyle} htmlFor="feedback-description">
              What happened, and what did you expect?
            </label>
            <textarea
              id="feedback-description"
              value={description}
              rows={5}
              maxLength={6000}
              onChange={(e) => setDescription(e.target.value)}
              style={fieldStyle}
            />

            <label style={labelStyle} htmlFor="feedback-notes">
              Anything else (optional)
            </label>
            <textarea
              id="feedback-notes"
              value={notes}
              rows={2}
              maxLength={2000}
              onChange={(e) => setNotes(e.target.value)}
              style={fieldStyle}
            />

            <div style={{ marginTop: 16 }}>
              <strong>Screenshots</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {images.map((img, index) => (
                  <div key={`${img.filename}-${index}`} style={{ position: 'relative' }}>
                    <img
                      src={img.data_url}
                      alt={`Attachment ${index + 1}`}
                      style={{
                        width: 120,
                        height: 80,
                        objectFit: 'cover',
                        border: '1px solid var(--color-border)',
                        borderRadius: 4,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setImages((prev) => prev.filter((_, i) => i !== index))}
                      aria-label={`Remove attachment ${index + 1}`}
                      style={{ position: 'absolute', top: 2, right: 2 }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {capturing && <span style={{ alignSelf: 'center' }}>Capturing the screen…</span>}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={recapture}
                  disabled={capturing || images.length >= MAX_FEEDBACK_SCREENSHOTS}
                >
                  Capture this screen
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={images.length >= MAX_FEEDBACK_SCREENSHOTS}
                >
                  Attach an image
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.heic,.heif,.avif"
                  multiple
                  hidden
                  onChange={(e) => onPickFiles(e.target.files)}
                />
              </div>
              {PASTE_HINT_SUPPORTED && (
                <p className="help" style={{ marginTop: 'var(--space-1)' }}>
                  You can also paste a copied image or screenshot with {PASTE_SHORTCUT}.
                </p>
              )}
            </div>

            {error && (
              <p role="alert" style={{ color: 'var(--color-error-border)', marginTop: 12 }}>
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button type="button" onClick={onSubmit} disabled={submitting}>
                {submitting ? 'Filing…' : 'File this report'}
              </button>
              <button type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
